import React from 'react';
import { BarChart, Trash2, Layers } from 'lucide-react';
import { formatNumber } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для сравнения сохраненных портфелей
 */
const ComparisonSidebar = ({ 
  savedPortfolios, 
  deleteSavedPortfolio, 
  loadSavedPortfolio,
  currencyTypes
}) => {
  // Если нет сохраненных портфелей
  if (!savedPortfolios || savedPortfolios.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-green-500" />
          Сравнение портфелей
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные варианты дивидендных портфелей для сравнения.
        </p>
      </div>
    );
  }
  
  // Получение символа валюты по коду
  const getCurrencySymbol = (currencyCode) => {
    const currency = currencyTypes.find(c => c.id === currencyCode);
    return currency ? currency.symbol : currencyCode;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-green-500" />
          Сравнение портфелей
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedPortfolios.length} {savedPortfolios.length === 1 ? 'портфель' : savedPortfolios.length < 5 ? 'портфеля' : 'портфелей'}
        </span>
      </div>

      <div className="space-y-4">
        {savedPortfolios.map((portfolio) => (
          <div 
            key={portfolio.id} 
            className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {portfolio.name}
              </h4>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Layers className="w-4 h-4" />}
                  onClick={() => loadSavedPortfolio(portfolio.id)}
                  title="Загрузить параметры"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="w-4 h-4 text-red-500" />}
                  onClick={() => deleteSavedPortfolio(portfolio.id)}
                  title="Удалить"
                />
              </div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <div className="flex justify-between mb-1">
                <span>Акции:</span>
                <span>
                  {formatNumber(portfolio.formData.sharesCount)} шт.
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Цена акции:</span>
                <span className="font-medium">
                  {getCurrencySymbol(portfolio.formData.currency)} {formatNumber(portfolio.formData.stockPrice, 2)}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Дивиденд на акцию:</span>
                <span>
                  {getCurrencySymbol(portfolio.formData.currency)} {formatNumber(portfolio.formData.dividendPerShare, 2)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Доход за {portfolio.results.yearsProjected} {pluralize(portfolio.results.yearsProjected, 'год', 'года', 'лет')}:</span>
                <span className="font-semibold text-green-600 dark:text-green-400">
                  {getCurrencySymbol(portfolio.formData.currency)} {formatNumber(portfolio.results.totalNetDividends, 0)}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Вспомогательная функция для склонения слов
const pluralize = (number, one, few, many) => {
  let n = Math.abs(number);
  n %= 100;
  if (n >= 5 && n <= 20) {
    return many;
  }
  n %= 10;
  if (n === 1) {
    return one;
  }
  if (n >= 2 && n <= 4) {
    return few;
  }
  return many;
};

export default ComparisonSidebar;